import React, { useCallback, useState } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    Checkbox,
    Typography,
    ListItemIcon,
    Button,
    IconButton,
    Alert,
    Chip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { TChannel } from '../../shared/types/Channel.type';
import { IHbDeal } from '../../shared/types/HbDeal.type';
import { t } from 'i18next';
import { weekDays } from '../../enums/days.enums';
import { scheduleType, TSchedule } from '../../shared/types/schedule.type';
import { useAuth } from '../../hooks/useAuth';
import { Edit } from '@mui/icons-material';
import { compact, isEmpty } from 'lodash';
import { useNotificationsSettings } from '../../hooks/useNotificationSettings';
import { toast } from 'react-toastify';
import { useGallopData } from '../../hooks/useGallopData';
import { LoadingButton } from '@mui/lab';
import palette from '../../theme/palette';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'
import { formatMetricIntoImperialDistance } from '../../shared/utils/conversion';

dayjs.extend(timezone)


const getBaseBest600 = () => {
    const userTz = dayjs.tz.guess();
    const highSpeedRegions = ['Australia', 'New_Zealand', 'Europe/London', 'Europe/Dublin'];

    const isHighSpeedRegion = highSpeedRegions.some(region => userTz.includes(region));

    return isHighSpeedRegion ? 42 : 46;
};


function ChannelList({ commChannels,
    isUserHasTrainingConfig,
    isUserHasGallopConfig,
    isUserHasStableConfig,
    pendingChannels

}: {
    commChannels: TChannel[],
    pendingChannels: string[],
    isUserHasTrainingConfig: boolean, isUserHasGallopConfig: boolean, isUserHasStableConfig: boolean
}) {
    const [checked, setChecked] = useState<Record<number, boolean>>(
        Object.fromEntries(commChannels.map((ch) => [ch.id, true]))
    );

    const { saveConfig, saveStable } = useNotificationsSettings()
    const { saveGallopReview } = useGallopData({ gallopDataQueryEnabled: false })
    const BACKOFFICE_LINK = process.env.REACT_APP_BACKOFFICE_URI

    const { auth } = useAuth()
const renderValueAndUnit = useCallback((v: number) => {
        let value = auth?.unit_system === 'metric' ? v : formatMetricIntoImperialDistance(v ?? 0, auth.imperial_distance_miles_only)
        let unit = auth?.unit_system === 'metric' ? t('units:meters') : null
        return compact([value, unit]).join(' ')
    }, [auth?.unit_system])

    const handleToggle = (id: number) => {
        setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const createConfigSyncLastHour = async () => {

        try {


            let communication_channels = getCommChannelIds()
        let valueDefault = `${renderValueAndUnit(600)}<${getBaseBest600()}${t('units:seconds')}`


            if (!communication_channels) return

            let schedules = weekDays.map(i => (
                { type: 'SYNCHRONISED_LAST_HOUR' as scheduleType, timezone: auth.timezone, day_of_week: i.value })
            )
            const dto: Partial<TSchedule> = { user: auth?.['@id'], name: t('defaultConfiguration', {
                    value: valueDefault, interpolation: {
                        escapeValue: false
                    }
                }), communication_channels, schedules }

            await saveConfig.mutateAsync(dto)
            toast(t('rights:success'), { type: 'success' })

        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }

    }



    const createConfigFixedHour = async () => {

        try {
            const hour = 12
            const minute = 0


            let communication_channels = getCommChannelIds()


            if (!communication_channels) return

            let schedules = weekDays.map(i => (
                { type: 'FIXED_TIME' as scheduleType, timezone: auth.timezone, day_of_week: i.value, hour, minute })
            )
            const dto: Partial<TSchedule> = { user: auth?.['@id'], name: t('repportType:gallop'), communication_channels, schedules }

            await saveGallopReview.mutateAsync(dto)
            toast(t('rights:success'), { type: 'success' })

        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }

    }

    const createStableConfig = async () => {

        try {
            const hour = 14
            const minute = 0


            let communication_channels = getCommChannelIds()


            if (!communication_channels) return

            let schedules = [{ type: 'MONTHLY' as scheduleType, timezone: auth.timezone, day_of_month: 1, hour, minute }]

            const dto: Partial<TSchedule> = { user: auth?.['@id'], name: t('stable:title:report'), max_best600: getBaseBest600(), communication_channels, schedules }

            await saveStable.mutateAsync(dto)
            toast(t('rights:success'), { type: 'success' })

        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }

    }



    const getCommChannelIds = () => {

        let pos = compact(Object.keys(checked).map((key) => checked[Number(key)] ? key : null))

        return commChannels.map(channel => channel['@id'])


    }



    const getIcon = (type: TChannel['type']) => {
        switch (type) {
            case 'EMAIL':
                return <Icon icon="mdi:email-outline" color="#1976d2" />; // blue email icon
            case 'WHATSAPP':
                return <Icon icon="mdi:whatsapp" color="#25D366" />; // green whatsapp
            case 'WHATSAPP_GROUP':
                return <Icon icon="mdi:account-group-outline" color="#FFB74D" />; // orange group
            default:
                return <Icon icon="mdi:chat-outline" />;
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                {t('pdfReports:emails:targets')}
            </Typography>
            <Box border={1} borderColor="divider" borderRadius={2} maxHeight={400} overflow="auto">
                <List disablePadding>
                    {commChannels.map((ch, idx) => (
                        <React.Fragment key={ch.id}>
                            <ListItem
                                secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                                        {getIcon(ch.type)}
                                    </Box>
                                }
                            >
                                {/* Left checkbox */}
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={checked[ch.id]}
                                        onChange={() => handleToggle(ch.id)}
                                    />
                                </ListItemIcon>

                                {/* Title + subtitle */}
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" fontWeight={600}>
                                            {ch.own_private_channel ? t('channel:onlyMe') : ch?.name || '-'}
                                        </Typography>
                                    }
                                    secondary={
                                        ch.targets.length > 0 ? ch.targets.join(', ') : 'No targets'
                                    }
                                />
                            </ListItem>

                            {(idx < commChannels.length - 1 && !isEmpty(pendingChannels)) && (
                                <Divider sx={{ bgcolor: 'divider', opacity: 0.2 }} />
                            )}
                        </React.Fragment>
                    ))}
                    {pendingChannels.map((ch, idx) => (
                        <React.Fragment key={ch.concat(idx.toString())}>
                            <ListItem
                                secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                                        {getIcon('WHATSAPP_GROUP')}
                                    </Box>
                                }
                            >
                                {/* Left checkbox */}
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        disabled
                                    />
                                </ListItemIcon>

                                {/* Title + subtitle */}
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" fontWeight={600}>
                                            {ch}
                                        </Typography>
                                    }
                                    secondary={
                                       <Chip icon={<Icon icon={"lets-icons:time-atack-light"}/>} size='small' color='warning' label={t('onboarding:createWaGroupFailedChip')}></Chip>
                                    }
                                />
                            </ListItem>

                            {idx < pendingChannels.length - 1 && (
                                <Divider sx={{ bgcolor: 'divider', opacity: 0.2 }} />
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Box>


            <Box display={'flex'} gap={2} alignItems={'center'} mt={2}>
                <Box gap={1} display={'flex'} flexDirection={'column'} flex={1}>
                    <LoadingButton loading={saveConfig.isLoading} onClick={createConfigSyncLastHour} fullWidth sx={{ minHeight: '100%' }} variant='contained'>{t('onboarding:createConfigSync')}</LoadingButton>
                    {isUserHasTrainingConfig && <Typography sx={{ display: 'flex', alignItems: 'center', gap: .5, alignSelf: 'center' }} variant='caption' color={palette.light.warning.main}>
                        <Icon icon='fluent-color:warning-32' />
                        {t('onboarding:userHasConfig')}</Typography>}

                </Box>
                <Box gap={1} display={'flex'} flexDirection={'column'} flex={1}>
                    <LoadingButton loading={saveGallopReview.isLoading} onClick={createConfigFixedHour} fullWidth sx={{ minHeight: '100%' }} variant='contained'>{t('onboarding:createConfigFixed')}</LoadingButton>
                    {isUserHasGallopConfig && <Typography sx={{ display: 'flex', alignItems: 'center', gap: .5, alignSelf: 'center' }} variant='caption' color={palette.light.warning.main}>
                        <Icon icon='fluent-color:warning-32' />
                        {t('onboarding:userHasConfig')}</Typography>}
                </Box>

                <Box gap={1} display={'flex'} flexDirection={'column'} flex={1}>
                    <LoadingButton loading={saveStable.isLoading} disabled={auth.type !== 'GALLOP'} onClick={createStableConfig} fullWidth sx={{ minHeight: '100%' }} variant='contained'>{t('onboarding:createStableConfig')}</LoadingButton>
                    {isUserHasStableConfig && <Typography sx={{ display: 'flex', alignItems: 'center', gap: .5, alignSelf: 'center' }} variant='caption' color={palette.light.warning.main}>
                        <Icon icon='fluent-color:warning-32' />
                        {t('onboarding:userHasConfig')}</Typography>}
                </Box>
            </Box>
            <Box display={'flex'} gap={1} alignItems={'center'} mt={2}>

                <Typography color={'#808080'} variant='subtitle2' display={'flex'} alignItems={'center'} gap={1}>
                    <Icon icon={'file-icons:moment-timezone'} />
                    {t('notification:field:timezone')} : {auth?.timezone}</Typography>


                <Typography href={`${BACKOFFICE_LINK}#/users/%2Fapi%2Fusers%2F${auth?.id}/edit`} target='_blank' component={'a'} color={'primary'} variant='subtitle2' display={'flex'} alignItems={'center'} gap={1}>
                    ( {t('button:edit')} )
                </Typography>
            </Box>
        </Box>
    );
}

export default ChannelList;
